<template>
  <v-container fluid fill-height>
    <v-row align="center" justify="center">
      <v-col cols="12">
        <v-card flat class="mx-auto public-form transparent">
          <v-card outlined id="reset-password">
            <v-card-text class="text-center">
              <v-img :src="$app.opts.authLogoSrc" :alt="$app.opts.title" max-height="250" contain />
            </v-card-text>

            <div v-if="!sent">
              <v-card-text class="text-center title">
                <h3>{{ $t('changePassword') }}</h3>
              </v-card-text>
              <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
              <v-card-text class="py-2">
                <v-form>
                  <v-text-field
                    append-icon="$vuetify.icons.values.password"
                    v-model.trim="form.password"
                    :error-messages="passwordErrors"
                    v-bind:label="$t('password')"
                    type="password"
                    required
                    @input="$v.form.password.$touch()"
                    @blur="$v.form.password.$touch()"
                  ></v-text-field>
                  <v-text-field
                    append-icon="$vuetify.icons.values.password"
                    v-model.trim="form.passwordConfirmation"
                    :error-messages="passwordConfirmationErrors"
                    v-bind:label="$t('passwordConfirmation')"
                    type="password"
                    required
                    @input="$v.form.passwordConfirmation.$touch()"
                    @blur="$v.form.passwordConfirmation.$touch()"
                  ></v-text-field>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn block color="accent" @click="doChangePassword" depressed>
                  {{ $t('changePassword') }}
                </v-btn>
              </v-card-actions>
            </div>

            <div v-if="sent">
              <v-card-text class="py-2 text-center title">
                <h3>{{ $t('passwordChanged') }}</h3>
              </v-card-text>
              <v-card-text class="py-2 text-center">{{ $t('passwordChangedMessage') }}</v-card-text>
            </div>
            <v-card-text class="py-2 text-center">
              {{ $t('labels.backTo') }}
              <br />
              <v-btn text color="default" :to="{ name: 'signin' }">
                {{ $t('signin') }}
              </v-btn>
            </v-card-text>
          </v-card>

          <v-card flat class="my-3 transparent text-center">
            <LanguageField use-buttons v-model="myLocale" immediate fieldClass="elevation-0" />
          </v-card>
        </v-card>
      </v-col>
    </v-row>
    <RefreshApp />
  </v-container>
</template>

<script>
import { required, sameAs } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import { BaseForm, FormErrors, LanguageField, RefreshApp } from '@argon/app/components'
import { NAMESPACE } from '../store'
import { NAMESPACE as ME_NAMESPACE } from '@argon/iam/me/store'

export default {
  extends: BaseForm,
  components: { FormErrors, LanguageField, RefreshApp },
  validations: {
    form: {
      password: { required },
      passwordConfirmation: { sameAsPassword: sameAs('password') }
    }
  },
  data: () => ({
    form: {
      password: '',
      passwordConfirmation: ''
    },
    sent: false
  }),
  computed: {
    ...mapGetters(ME_NAMESPACE, ['locale']),
    myLocale: {
      get() {
        return this.locale
      },
      set(v) {
        this.setLocale(v)
      }
    },
    passwordErrors() {
      const errors = []
      if (!this.$v.form.password.$dirty) return errors
      !this.$v.form.password.required && errors.push(this.$t('required'))
      this.getServerErrors('password', errors)
      return errors
    },
    passwordConfirmationErrors() {
      const errors = []
      if (!this.$v.form.passwordConfirmation.$dirty) return errors
      !this.$v.form.passwordConfirmation.sameAsPassword && errors.push(this.$t('mismatch'))
      this.getServerErrors('passwordConfirmation', errors)
      return errors
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['changePassword']),
    ...mapActions(ME_NAMESPACE, ['setLocale']),
    doChangePassword() {
      if (this.$v.form.$invalid) {
        this.$v.form.$touch()
      }
      const payload = {
        token: this.token,
        password: this.form.password
      }
      this.changePassword(payload)
        .then((resp) => {
          this.sent = true
        })
        .catch(this.setServerErrors)
    }
  },
  props: {
    token: {
      type: String,
      default: 'invalidToken'
    }
  }
}
</script>

<i18n>
{
  "en": {
    "backTo": "Back to",
    "changePassword": "Change my Password",
    "mismatch": "@:labels.mismatch",
    "password": "@:labels.password",
    "passwordChanged": "Password Changed",
    "passwordChangedMessage": "Your password has been changed successfully",
    "passwordConfirmation": "Password Confirmation",
    "required": "@:labels.required",
    "signin": "@:labels.signin",
    "token": "@:labels.token"
  },
  "fr": {
    "backTo": "Retourner à",
    "changePassword": "Changer mon mot de passe",
    "mismatch": "@:labels.mismatch",
    "password": "@:labels.password",
    "passwordChanged": "Mot de passe changé",
    "passwordChangedMessage": "Votre mot de passe a été changé avec succès",
    "passwordConfirmation": "Confirmation du mot de passe",
    "required": "@:labels.required",
    "signin": "@:labels.signin",
    "token": "@:labels.token"
  }
}
</i18n>
